<!--
  Add/remove aliases inline in the expanded row. A duplicate or a name-collision is refused
  server-side (journal.merchant_alias_already_exists / journal.merchant_alias_collides) and
  surfaced here as a reason next to the input, never as a bare failure. Removing an alias asks
  for a deliberate confirmation first - myWealth's own screen has the confirm string translated
  and unused, and removes instantly (feature-merchants.md §2.2.2, D-MER-11).
-->
<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import type { Merchant } from '../composables/use-merchants.ts'
import { isAliasAlreadyExists, isAliasCollision, problemDetail } from '../internal/problem.ts'

const props = defineProps<{
  merchant: Merchant
  addAlias: (alias: string) => Promise<Merchant>
  removeAlias: (alias: string) => Promise<Merchant>
}>()

const { t } = useI18n()

const newAlias = ref('')
const isAdding = ref(false)
const addError = ref<Error | null>(null)
const pendingRemoval = ref<string | undefined>(undefined)
const isRemoving = ref(false)

function addErrorMessage(): string | undefined {
  if (addError.value === null) return undefined
  if (isAliasAlreadyExists(addError.value))
    return t('merchants.aliasErrorAlreadyExists', { name: props.merchant.name })
  if (isAliasCollision(addError.value)) return t('merchants.aliasErrorCollides')
  return problemDetail(addError.value) ?? t('merchants.aliasErrorGeneric')
}

async function onAddAlias(): Promise<void> {
  const alias = newAlias.value.trim()
  if (alias === '' || isAdding.value) return
  isAdding.value = true
  addError.value = null
  try {
    await props.addAlias(alias)
    newAlias.value = ''
  } catch (error) {
    addError.value = error as Error
  } finally {
    isAdding.value = false
  }
}

function requestRemoval(alias: string): void {
  pendingRemoval.value = alias
}

function cancelRemoval(): void {
  pendingRemoval.value = undefined
}

async function confirmRemoval(alias: string): Promise<void> {
  isRemoving.value = true
  try {
    await props.removeAlias(alias)
  } finally {
    isRemoving.value = false
    pendingRemoval.value = undefined
  }
}
</script>

<template>
  <div class="flex flex-col gap-[var(--space-inline)]">
    <p v-if="merchant.aliases.length === 0" class="text-[color:var(--color-text-muted)]">
      {{ t('merchants.aliasesEmpty') }}
    </p>
    <ul v-else class="flex flex-wrap gap-[var(--space-inline)]">
      <li v-for="alias in merchant.aliases" :key="alias">
        <div
          v-if="pendingRemoval === alias"
          class="flex items-center gap-[var(--space-hair)] rounded-[var(--radius-control)] border border-[color:var(--color-danger)] bg-[color:var(--color-surface)] px-[var(--space-inline)] py-[var(--space-hair)]"
        >
          <span class="text-[length:var(--text-step-1)] text-[color:var(--color-text)]">
            {{ t('merchants.aliasRemoveConfirm', { alias }) }}
          </span>
          <button
            type="button"
            :disabled="isRemoving"
            class="text-[length:var(--text-step-1)] font-semibold text-[color:var(--color-danger)] disabled:opacity-60"
            @click="confirmRemoval(alias)"
          >
            {{ isRemoving ? t('merchants.aliasRemoving') : t('merchants.aliasRemoveConfirmYes') }}
          </button>
          <button
            type="button"
            :disabled="isRemoving"
            class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)] disabled:opacity-60"
            @click="cancelRemoval"
          >
            {{ t('merchants.aliasRemoveConfirmCancel') }}
          </button>
        </div>
        <button
          v-else
          type="button"
          :aria-label="t('merchants.aliasRemove', { alias })"
          class="flex items-center gap-[var(--space-hair)] rounded-[var(--radius-control)] bg-[color:var(--color-surface-raised)] px-[var(--space-inline)] py-[var(--space-hair)] text-[length:var(--text-step-1)] text-[color:var(--color-text)]"
          @click="requestRemoval(alias)"
        >
          {{ alias }}
          <span aria-hidden="true">×</span>
        </button>
      </li>
    </ul>

    <form class="flex items-start gap-[var(--space-inline)]" @submit.prevent="onAddAlias">
      <label class="sr-only" :for="`alias-input-${merchant.id}`">{{ t('merchants.aliasPlaceholder') }}</label>
      <input
        :id="`alias-input-${merchant.id}`"
        v-model="newAlias"
        type="text"
        maxlength="255"
        :placeholder="t('merchants.aliasPlaceholder')"
        class="min-h-[var(--density-control)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-cell)] text-[length:var(--text-step-2)] text-[color:var(--color-text)]"
      />
      <button
        type="submit"
        :disabled="isAdding || newAlias.trim() === ''"
        class="min-h-[var(--density-control)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] px-[var(--space-cell)] text-[length:var(--text-step-2)] text-[color:var(--color-text)] disabled:opacity-60"
      >
        {{ isAdding ? t('merchants.aliasAdding') : t('merchants.aliasAdd') }}
      </button>
    </form>
    <p
      v-if="addError !== null"
      role="alert"
      class="text-[length:var(--text-step-1)] text-[color:var(--color-danger)]"
    >
      {{ addErrorMessage() }}
    </p>
  </div>
</template>
